import React, { useEffect, useRef, useState } from 'react';
import { ExclamationIcon, ChatIcon } from '@heroicons/react/outline';
import { Crisp } from 'crisp-sdk-web';

interface AccessBlockedModalProps {
	isOpen: boolean;
}

const AccessBlockedModal: React.FC<AccessBlockedModalProps> = ({ isOpen }) => {
	const [isLoading, setIsLoading] = useState(false);
	const modalRef = useRef<HTMLDivElement>(null);
	const intervalRef = useRef<NodeJS.Timeout | null>(null);
	const mutationObserverRef = useRef<MutationObserver | null>(null);

	const handleContactSupport = async () => {
		setIsLoading(true);
		try {
			await Crisp.chat.show();
			await Crisp.chat.open();
			// Small delay to ensure chat loads
			setTimeout(() => {
				setIsLoading(false);
			}, 1000);
		} catch (error) {
			console.error('Error opening Crisp chat:', error);
			setIsLoading(false);
		}
	};

	// Security: Prevent bypassing via dev tools
	useEffect(() => {
		if (!isOpen) return;

		// 1. Disable right-click context menu
		const disableContextMenu = (e: MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			return false;
		};

		// 2. Disable key combinations (F12, Ctrl+Shift+I, etc.)
		const disableDevTools = (e: KeyboardEvent) => {
			if (
				e.key === 'F12' ||
				(e.ctrlKey && e.shiftKey && e.key === 'I') ||
				(e.ctrlKey && e.shiftKey && e.key === 'C') ||
				(e.ctrlKey && e.shiftKey && e.key === 'J') ||
				(e.ctrlKey && e.key === 'u') ||
				(e.ctrlKey && e.key === 'U')
			) {
				e.preventDefault();
				e.stopPropagation();
				return false;
			}
			return;
		};

		// 3. Monitor DOM mutations to prevent modal removal
		const observeModalRemoval = () => {
			if (!modalRef.current) return;

			mutationObserverRef.current = new MutationObserver((mutations) => {
				mutations.forEach((mutation) => {
					if (mutation.type === 'childList') {
						// Check if modal was removed
						const modalExists = document.querySelector('[data-access-blocked-modal]');
						if (!modalExists && isOpen) {
							// Re-inject modal if it was removed
							window.location.reload();
						}
					}
				});
			});

			mutationObserverRef.current.observe(document.body, {
				childList: true,
				subtree: true,
			});
		};

		// 4. Periodic checks to ensure modal is still present
		intervalRef.current = setInterval(() => {
			const modalExists = document.querySelector('[data-access-blocked-modal]');
			if (!modalExists && isOpen) {
				window.location.reload();
			}
		}, 1000);

		// 5. Monitor window focus to detect dev tools
		const checkDevTools = () => {
			const threshold = 160;
			if (window.outerHeight - window.innerHeight > threshold || window.outerWidth - window.innerWidth > threshold) {
				// Possible dev tools detected
				console.clear();
				console.log('Access monitoring active');
			}
		};

		// 6. Disable text selection
		const disableSelection = (e: Event) => {
			e.preventDefault();
			return false;
		};

		// Apply all security measures
		document.addEventListener('contextmenu', disableContextMenu);
		document.addEventListener('keydown', disableDevTools);
		document.addEventListener('selectstart', disableSelection);
		document.addEventListener('dragstart', disableSelection);
		window.addEventListener('resize', checkDevTools);
		window.addEventListener('focus', checkDevTools);

		observeModalRemoval();

		// Cleanup
		return () => {
			document.removeEventListener('contextmenu', disableContextMenu);
			document.removeEventListener('keydown', disableDevTools);
			document.removeEventListener('selectstart', disableSelection);
			document.removeEventListener('dragstart', disableSelection);
			window.removeEventListener('resize', checkDevTools);
			window.removeEventListener('focus', checkDevTools);

			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
			if (mutationObserverRef.current) {
				mutationObserverRef.current.disconnect();
			}
		};
	}, [isOpen]);

	if (!isOpen) return null;

	return (
		<div
			ref={modalRef}
			data-access-blocked-modal="true"
			className="fixed inset-0 z-50 flex items-center justify-center"
			style={{
				position: 'fixed',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				zIndex: 999999, // Maximum z-index value
				background: 'rgba(0, 0, 0, 0.9)',
				userSelect: 'none',
				WebkitUserSelect: 'none',
				MozUserSelect: 'none',
				msUserSelect: 'none',
				pointerEvents: 'all',
			}}
		>
			<div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
				<div className="flex items-center justify-center mb-6">
					<div className="bg-red-100 rounded-full p-3">
						<ExclamationIcon className="h-8 w-8 text-red-600" />
					</div>
				</div>

				<div className="text-center mb-6">
					<h2 className="text-2xl font-bold text-gray-900 mb-4">Access Blocked</h2>
					<p className="text-gray-600 leading-relaxed">
						Your access to CardClan has been temporarily blocked. If you believe this is an error or need assistance,
						please contact our support team for immediate help.
					</p>
				</div>

				<div className="flex justify-center">
					<button
						onClick={handleContactSupport}
						disabled={isLoading}
						className={`inline-flex items-center px-6 py-3 ${
							isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo hover:bg-indigo700'
						} text-white font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-indigo focus:ring-offset-2 transition-colors duration-200`}
					>
						{isLoading ? (
							<>
								<svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
									<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
									<path
										className="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
									/>
								</svg>
								Loading...
							</>
						) : (
							<>
								<ChatIcon className="h-5 w-5 mr-2" />
								Contact Support
							</>
						)}
					</button>
				</div>

				<div className="mt-4 text-center">
					<p className="text-sm text-gray-500">Our support team is available to help resolve this issue quickly.</p>
				</div>
			</div>
		</div>
	);
};

export default AccessBlockedModal;
